import { type FirebaseApp, getApps, initializeApp } from 'firebase/app';
import { type Auth, connectAuthEmulator, getAuth } from 'firebase/auth';
import { type Firestore, connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { type FirebaseStorage, connectStorageEmulator, getStorage } from 'firebase/storage';

/**
 * Firebase SDK initialisation — the only place the SDK is constructed
 * (blueprint §4.1: platform SDKs live in `infrastructure/`).
 *
 * **The factory declares the configuration it needs; it does not import it.**
 * An earlier version imported the config type from `app/config`, which §3.2
 * forbids — `infrastructure/` must never depend on `app/`. The boundary checker
 * rejected it, and it was right to: the direction of that import is what would
 * make this module unusable from a test or a script that has no composition
 * root. `app/` validates the environment and passes the result in.
 */

export interface FirebaseClientConfig {
  readonly apiKey: string;
  readonly authDomain: string;
  readonly projectId: string;
  readonly storageBucket: string;
  readonly messagingSenderId: string;
  readonly appId: string;
}

export interface EmulatorClientConfig {
  readonly enabled: boolean;
  readonly host: string;
  readonly authPort: number;
  readonly firestorePort: number;
  readonly storagePort: number;
}

export interface FirebaseInit {
  readonly firebase: FirebaseClientConfig;
  readonly emulator: EmulatorClientConfig;
}

export interface FirebaseServices {
  readonly app: FirebaseApp;
  readonly auth: Auth;
  readonly firestore: Firestore;
  readonly storage: FirebaseStorage;
}

let services: FirebaseServices | null = null;

export function initialiseFirebase(config: FirebaseInit): FirebaseServices {
  // Vite's hot reload re-executes modules; initialising twice throws.
  if (services) return services;

  const app = getApps()[0] ?? initializeApp(config.firebase);
  const auth = getAuth(app);
  const firestore = getFirestore(app);
  const storage = getStorage(app);

  if (config.emulator.enabled) {
    const { host, authPort, firestorePort, storagePort } = config.emulator;

    // `disableWarnings` only silences the banner the SDK prints; it does not
    // relax anything. The emulator is opt-in via an env flag, so a production
    // build cannot reach it by accident.
    connectAuthEmulator(auth, `http://${host}:${String(authPort)}`, {
      disableWarnings: true,
    });
    connectFirestoreEmulator(firestore, host, firestorePort);
    connectStorageEmulator(storage, host, storagePort);
  }

  services = { app, auth, firestore, storage };
  return services;
}

/**
 * For tests only: forgets the cached instance so a fresh config can be applied.
 * Production code has no reason to call this.
 */
export function resetFirebaseForTests(): void {
  services = null;
}
